import { Link, useNavigate } from 'react-router-dom'
import type { ReactNode } from 'react'
import type { CaseListItem } from '../lib/api'
import { SeverityBadge, StatusBadge } from './Badges'
import { formatDate } from '../lib/format'

export interface Stat {
  label: string
  value: string
  accent?: string
}

// Shared layout for technician / subcontractor profiles: header, contact rows,
// stat tiles, and a case-history table.
export function ProfileView({
  backTo,
  backLabel,
  title,
  subtitle,
  headerRight,
  filter,
  contact,
  contactEditor,
  stats,
  cases,
}: {
  backTo: string
  backLabel: string
  title: string
  subtitle?: ReactNode
  headerRight?: ReactNode
  filter?: ReactNode
  contact: { label: string; value: ReactNode }[]
  contactEditor?: ReactNode
  stats: Stat[]
  cases: CaseListItem[]
}) {
  const navigate = useNavigate()
  return (
    <div>
      <Link to={backTo} className="link-btn">
        ← {backLabel}
      </Link>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginTop: 8 }}>
        <div className="page-title" style={{ margin: 0 }}>{title}</div>
        {headerRight && <span style={{ marginLeft: 'auto' }}>{headerRight}</span>}
      </div>
      {subtitle && <p className="page-sub">{subtitle}</p>}

      {filter && <div style={{ margin: '12px 0' }}>{filter}</div>}

      <div style={{ display: 'grid', gap: 16, marginTop: 12 }}>
        <div className="stat-row">
          {stats.map((s) => (
            <div className="card stat" key={s.label}>
              <div className="stat-label">{s.label}</div>
              <div className="stat-value" style={s.accent ? { color: s.accent } : undefined}>{s.value}</div>
            </div>
          ))}
        </div>

        {contactEditor ? (
          <div className="card">{contactEditor}</div>
        ) : (
          contact.length > 0 && (
            <div className="card">
              <div style={{ fontWeight: 700, marginBottom: 12 }}>Contact</div>
              {contact.map((r) => (
                <div key={r.label} style={{ display: 'flex', gap: 8, padding: '4px 0', fontSize: 14 }}>
                  <div style={{ width: 120, color: 'var(--gray)', flexShrink: 0 }}>{r.label}</div>
                  <div style={{ minWidth: 0, wordBreak: 'break-word' }}>{r.value}</div>
                </div>
              ))}
            </div>
          )
        )}

        <div className="card" style={{ padding: 0 }}>
          <div style={{ fontWeight: 700, padding: '16px 20px 0' }}>Case history ({cases.length})</div>
          {cases.length === 0 ? (
            <div style={{ padding: 20, color: 'var(--gray)' }}>No cases yet.</div>
          ) : (
            <div className="table-wrap">
              <table className="table" style={{ marginTop: 8 }}>
                <thead>
                  <tr>
                    <th>Case #</th>
                    <th>Address</th>
                    <th>Incident date</th>
                    <th>Status</th>
                    <th>Severity</th>
                  </tr>
                </thead>
                <tbody>
                  {cases.map((k) => (
                    <tr key={k.id} className="row-link" onClick={() => navigate(`/app/cases/${k.id}`)}>
                      <td style={{ fontWeight: 600 }}>{k.case_number}</td>
                      <td>{k.address ?? '—'}</td>
                      <td>{formatDate(k.incident_date)}</td>
                      <td><StatusBadge status={k.status} /></td>
                      <td><SeverityBadge severity={k.severity} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
